import { Link } from "react-router-dom";
import { motion } from "@/lib/motion";
import { useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { ChevronDown, Mail, Phone, MapPin, Facebook, Twitter, Instagram, Linkedin, Youtube, Sparkles, Zap, Shield, TrendingUp, ArrowLeft, Clock } from "lucide-react";
import itqanLogo from "@/assets/itqan-logo-new.png";
const Footer = () => {
  const isMobile = useIsMobile();
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const currentYear = new Date().getFullYear();
  const toggleSection = (section: string) => {
    if (isMobile) {
      setExpandedSection(expandedSection === section ? null : section);
    }
  };
  const links = {
    company: [{
      label: "من نحن",
      href: "/about"
    }, {
      label: "خدماتنا",
      href: "/services"
    }, {
      label: "كيف نعمل",
      href: "/how-we-work"
    }, {
      label: "أعمالنا",
      href: "/work"
    }, {
      label: "الموارد",
      href: "/resources"
    }, {
      label: "المقارنات",
      href: "/comparisons"
    }, {
      label: "المدونة",
      href: "/blog"
    }, {
      label: "الأسئلة الشائعة",
      href: "/faq"
    }, {
      label: "تواصل معنا",
      href: "/contact"
    }, {
      label: "شركاء نجاحنا",
      href: "/partners"
    }],
    legal: [{
      label: "سياسة الخصوصية",
      href: "/privacy"
    }, {
      label: "الشروط والأحكام",
      href: "/terms"
    }]
  };
  const contactInfo = [{
    icon: Mail,
    text: "info@itqan.agency",
    href: "mailto:info@itqan.agency"
  }, {
    icon: Phone,
    text: "+966 50 123 4567",
    href: "tel:+966501234567"
  }, {
    icon: MapPin,
    text: "الرياض، السعودية"
  }];
  const socialLinks = [{
    icon: Facebook,
    href: "#",
    label: "Facebook"
  }, {
    icon: Twitter,
    href: "#",
    label: "Twitter"
  }, {
    icon: Instagram,
    href: "#",
    label: "Instagram"
  }, {
    icon: Linkedin,
    href: "#",
    label: "LinkedIn"
  }, {
    icon: Youtube,
    href: "#",
    label: "YouTube"
  }];
  return <footer className="relative border-t border-foreground/10 bg-surface overflow-hidden">
      {/* Multi-Layer Spectacular Background Effects */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Dynamic Gradient Orbs */}
        <motion.div className="absolute top-0 right-1/4 w-96 h-96 rounded-full" style={{
        background: 'radial-gradient(circle, hsl(var(--primary)) 0%, transparent 70%)',
        opacity: 0.08
      }} animate={{
        scale: [1, 1.3, 1],
        x: [0, 50, 0],
        y: [0, 30, 0],
        opacity: [0.08, 0.15, 0.08]
      }} transition={{
        duration: 12,
        repeat: Infinity,
        ease: "easeInOut"
      }} />
        
        <motion.div className="absolute bottom-0 left-1/3 w-80 h-80 rounded-full" style={{
        background: 'radial-gradient(circle, hsl(var(--accent)) 0%, transparent 70%)',
        opacity: 0.06
      }} animate={{
        scale: [1, 1.4, 1],
        x: [0, -40, 0],
        y: [0, -25, 0],
        opacity: [0.06, 0.12, 0.06]
      }} transition={{
        duration: 15,
        repeat: Infinity,
        ease: "easeInOut",
        delay: 2
      }} />

        <motion.div className="absolute top-1/2 left-1/2 w-72 h-72 rounded-full" style={{
        background: 'radial-gradient(circle, hsl(var(--sand)) 0%, transparent 70%)',
        opacity: 0.05
      }} animate={{
        scale: [1, 1.5, 1],
        rotate: [0, 180, 360],
        opacity: [0.05, 0.1, 0.05]
      }} transition={{
        duration: 20,
        repeat: Infinity,
        ease: "easeInOut",
        delay: 1
      }} />

        {/* Animated Grid Pattern */}
        <motion.div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'linear-gradient(hsl(var(--primary) / 0.2) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary) / 0.2) 1px, transparent 1px)',
        backgroundSize: '50px 50px'
      }} animate={{
        backgroundPosition: ['0px 0px', '50px 50px']
      }} transition={{
        duration: 20,
        repeat: Infinity,
        ease: "linear"
      }} />

        {/* Glowing Particles */}
        {[...Array(isMobile ? 20 : 40)].map((_, i) => <motion.div key={`particle-${i}`} className="absolute rounded-full" style={{
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        width: '3px',
        height: '3px',
        background: 'hsl(var(--sand))',
        boxShadow: '0 0 12px hsl(var(--sand) / 0.6)'
      }} animate={{
        y: [0, -40, 0],
        x: [0, Math.random() * 20 - 10, 0],
        opacity: [0, 0.8, 0],
        scale: [0, 1.5, 0]
      }} transition={{
        duration: 3 + Math.random() * 3,
        delay: Math.random() * 3,
        repeat: Infinity,
        ease: "easeInOut"
      }} />)}

        {/* Diagonal Flowing Lines */}
        {[0, 1, 2, 3].map(i => <motion.div key={`line-${i}`} className="absolute h-px bg-gradient-to-r from-transparent via-sand/30 to-transparent" style={{
        top: `${20 + i * 20}%`,
        left: 0,
        right: 0
      }} animate={{
        scaleX: [0, 1, 0],
        x: ['-100%', '0%', '100%'],
        opacity: [0, 0.5, 0]
      }} transition={{
        duration: 6,
        repeat: Infinity,
        repeatDelay: 2,
        ease: "easeInOut",
        delay: i * 1.5
      }} />)}

        {/* Pulsing Circles */}
        {[...Array(8)].map((_, i) => <motion.div key={`circle-${i}`} className="absolute rounded-full border border-sand/15" style={{
        left: `${10 + i * 12}%`,
        top: `${15 + i % 3 * 30}%`,
        width: '100px',
        height: '100px'
      }} animate={{
        scale: [1, 2.5, 1],
        opacity: [0.15, 0, 0.15]
      }} transition={{
        duration: 8,
        repeat: Infinity,
        ease: "easeInOut",
        delay: i * 1
      }} />)}

        {/* Rotating Stars */}
        {[...Array(6)].map((_, i) => <motion.div key={`star-${i}`} className="absolute" style={{
        left: `${15 + i * 15}%`,
        top: `${10 + i % 4 * 25}%`
      }} animate={{
        rotate: [0, 360],
        scale: [0.8, 1.2, 0.8],
        opacity: [0.1, 0.3, 0.1]
      }} transition={{
        duration: 10,
        repeat: Infinity,
        ease: "linear",
        delay: i * 0.5
      }}>
            <div className="w-3 h-3 bg-sand/20 rotate-45" style={{
          clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)'
        }} />
          </motion.div>)}

        {/* Flowing Wave Animation */}
        {[0, 1, 2, 3, 4].map(i => <motion.div key={`wave-${i}`} className="absolute bottom-0 h-32 opacity-[0.04]" style={{
        left: `${i * 20}%`,
        width: '30%',
        background: 'linear-gradient(to top, hsl(var(--sand)) 0%, transparent 100%)'
      }} animate={{
        scaleY: [1, 1.8, 1],
        opacity: [0.04, 0.08, 0.04]
      }} transition={{
        duration: 5,
        repeat: Infinity,
        ease: "easeInOut",
        delay: i * 0.6
      }} />)}

        {/* Sparkle Effect */}
        {[...Array(15)].map((_, i) => <motion.div key={`sparkle-${i}`} className="absolute w-1 h-1 bg-sand rounded-full" style={{
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        boxShadow: '0 0 8px hsl(var(--sand))'
      }} animate={{
        scale: [0, 1, 0],
        opacity: [0, 1, 0]
      }} transition={{
        duration: 2,
        repeat: Infinity,
        delay: i * 0.3,
        ease: "easeInOut"
      }} />)}

        {/* Radial Pulse from Center */}
        <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" style={{
        width: '200px',
        height: '200px',
        borderRadius: '50%',
        border: '2px solid hsl(var(--sand) / 0.1)'
      }} animate={{
        scale: [1, 3, 1],
        opacity: [0.1, 0, 0.1]
      }} transition={{
        duration: 6,
        repeat: Infinity,
        ease: "easeOut"
      }} />
      </div>

      <div className="container mx-auto px-4 py-6 md:py-8 relative z-10">
        {/* Main Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 lg:gap-4 xl:gap-3 mb-5 md:mb-6 lg:max-w-6xl lg:mx-auto items-start">
          
          {/* Brand Section - 1 col aligned with others */}
          <motion.div className="lg:col-span-1 text-center md:text-right flex flex-col" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4
        }}>
            <Link to="/" className="group inline-block mb-2 hover:scale-105 transition-all duration-300">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-accent/10 to-sand/10 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <img src={itqanLogo} alt="ItQan. Agency - إتقان" width="162" height="162" className="relative h-[124px] md:h-[140px] w-auto object-contain mx-auto md:mx-0 md:ml-8 transition-all duration-300 group-hover:drop-shadow-[0_0_20px_rgba(90,168,255,0.5)]" />
              </div>
            </Link>
            <p className="text-foreground/70 text-[10px] md:text-xs leading-relaxed mb-2 max-w-[200px] mx-auto md:mx-0">
              منصة اتقان السعودية | استراتيجيات أداء، محتوى مؤثر، ونتائج قابلة للقياس
            </p>
            
            {/* Social Links */}
            <div className="flex gap-1.5 justify-center md:justify-start">
              {socialLinks.map(social => <a key={social.label} href={social.href} className="w-8 h-8 rounded-lg glass flex items-center justify-center hover:bg-sand/10 transition-all hover:scale-110 border border-foreground/5" aria-label={social.label}>
                  <social.icon className="w-3.5 h-3.5 text-foreground/60 hover:text-sand transition-colors" />
                </a>)}
            </div>
          </motion.div>

          {/* Company Links - 1 col */}
          <motion.div className="lg:col-span-1 flex flex-col" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: 0.1
        }}>
            <h3 className="font-display font-bold text-[13.6px] md:text-[16.15px] text-sand mb-2 pb-1 border-b border-sand/20">الشركة</h3>
            <div className="space-y-1">
              {links.company.map(link => <div key={link.href}>
                  <Link to={link.href} className="text-[10.2px] md:text-[11.9px] text-foreground/70 hover:text-sand transition-all block py-0.5 hover:translate-x-1 duration-300 group flex items-center gap-1">
                    <span className="w-0.5 h-0.5 rounded-full bg-sand/0 group-hover:bg-sand transition-all duration-300" />
                    {link.label}
                  </Link>
                </div>)}
            </div>
          </motion.div>

          {/* Legal Links - 1 col */}
          <motion.div className="lg:col-span-1 flex flex-col" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: 0.2
        }}>
            <h3 className="font-display font-bold text-[13.6px] md:text-[16.15px] text-sand mb-2 pb-1 border-b border-sand/20">قانوني</h3>
            <div className="space-y-1">
              {links.legal.map(link => <div key={link.href}>
                  <Link to={link.href} className="text-[10.2px] md:text-[11.9px] text-foreground/70 hover:text-sand transition-all block py-0.5 hover:translate-x-1 duration-300 group flex items-center gap-1">
                    <span className="w-0.5 h-0.5 rounded-full bg-sand/0 group-hover:bg-sand transition-all duration-300" />
                    {link.label}
                  </Link>
                </div>)}
            </div>
          </motion.div>

          {/* Contact - 1 col */}
          <motion.div className="lg:col-span-1 flex flex-col" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: 0.3
        }}>
            <h3 className="font-display font-bold text-[13.6px] md:text-[16.15px] text-sand mb-2 pb-1 border-b border-sand/20">تواصل معنا</h3>
            
            {/* Contact Info */}
            <div className="space-y-2">
              {contactInfo.map((contact, index) => <div key={index} className="flex items-start gap-1.5 text-[10.2px] md:text-[11.9px] text-foreground/70 hover:text-sand transition-colors group">
                  <div className="w-6 h-6 rounded-lg glass flex items-center justify-center flex-shrink-0 group-hover:bg-sand/10 transition-all border border-foreground/5">
                    <contact.icon className="w-3 h-3 group-hover:scale-110 transition-transform" />
                  </div>
                  <div className="flex-1 pt-0.5">
                    {contact.href ? <a href={contact.href} className="hover:underline break-all">
                        {contact.text}
                      </a> : <span className="break-words">{contact.text}</span>}
                  </div>
                </div>)}
            </div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-4 md:pt-5 border-t border-foreground/10">
          <div className="flex flex-col md:flex-row items-center justify-center md:justify-between gap-2 md:gap-3 text-center md:text-right">
            {/* Copyright */}
            <motion.div initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }}>
              <Link 
                to="/" 
                className="inline-block text-[10px] md:text-xs text-foreground/60 hover:text-foreground/80 font-cairo font-medium transition-colors duration-300 hover:scale-[1.02] transform"
              >
                © {currentYear} <span className="font-semibold">ItQan. Agency</span> — نصنع نتائج يمكن قياسها
              </Link>
            </motion.div>

            {/* Made with Love in KSA */}
            <motion.div className="flex items-center gap-1.5 text-[10px] md:text-xs font-cairo font-bold" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }}>
              <span className="text-foreground/70">صُنع بكل</span>
              <motion.span className="inline-block text-sm" animate={{
              scale: [1, 1.2, 1]
            }} transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "easeInOut"
            }}>
                ❤️
              </motion.span>
              <span className="text-foreground/70">وفخر في</span>
              <span className="font-extrabold text-sand bg-sand/5 px-1.5 py-0.5 rounded text-[10px] md:text-xs">المملكة العربية السعودية</span>
              <motion.span className="text-xs" animate={{
              rotate: [0, 10, -10, 0]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}>
                🇸🇦
              </motion.span>
            </motion.div>

            {/* Rights Reserved */}
            <motion.p className="text-[10px] md:text-xs text-foreground/60 font-cairo" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }}>
              جميع الحقوق محفوظة
            </motion.p>
          </div>
        </div>
      </div>
    </footer>;
};
export default Footer;